'use client';

import React, { useMemo, useState } from 'react';
import useSWR from 'swr';
import { NavBar } from '@/components/NavBar';
import { StatusBar } from '@/components/StatusBar';
import { PhotoCard } from '@/components/PhotoCard';
import { BrowseDialog } from '@/components/BrowseDialog';
import { TrainDialog } from '@/components/TrainDialog';
import {
  AXES, Axis, AppConfig, Batch, Photo, Progress, Stats,
  assembleName, finalValues,
} from '@/lib/types';

const fetcher = (url: string) => fetch(url).then((r) => r.json());

export default function Home() {
  const [batch, setBatch] = useState<string | null>(null);
  const [scanDir, setScanDir] = useState('');
  const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [zoom, setZoom] = useState<Photo | null>(null);
  const [showApply, setShowApply] = useState(false);
  const [showBrowse, setShowBrowse] = useState(false);
  const [trainDir, setTrainDir] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const { data: config } = useSWR<AppConfig>('/api/config', fetcher);
  const { data: batches, mutate: mutateBatches } = useSWR<Batch[]>('/api/batches', fetcher);
  const { data: progress } = useSWR<Progress>('/api/progress', fetcher, {
    refreshInterval: (p) => (p?.running ? 1500 : 5000),
  });
  const running = progress?.running ?? false;

  const activeBatch = batch ?? batches?.[0]?.batch ?? null;
  const { data: photos, mutate: mutatePhotos } = useSWR<Photo[]>(
    activeBatch ? `/api/photos?batch=${encodeURIComponent(activeBatch)}` : null,
    fetcher,
    { refreshInterval: running ? 2500 : 0 }
  );
  const { data: stats, mutate: mutateStats } = useSWR<Stats>(
    activeBatch ? `/api/stats?batch=${encodeURIComponent(activeBatch)}` : null,
    fetcher
  );

  const groups = useMemo(() => {
    if (!photos || !config) return [];
    const map = new Map<string, Photo[]>();
    for (const p of photos) {
      const key =
        p.status === 'done'
          ? assembleName(config, finalValues(p))
          : p.status === 'error'
            ? 'Errors'
            : 'Not classified yet';
      map.set(key, [...(map.get(key) ?? []), p]);
    }
    return [...map.entries()].sort((x, y) => {
      const special = (k: string) => (k === 'Errors' ? 2 : k === 'Not classified yet' ? 1 : 0);
      return special(x[0]) - special(y[0]) || y[1].length - x[1].length || x[0].localeCompare(y[0]);
    });
  }, [photos, config]);

  const refresh = () => {
    void mutatePhotos();
    void mutateBatches();
    void mutateStats();
  };

  async function post(url: string, body?: unknown) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error ?? `${url} failed`);
    return data;
  }

  async function handleScan() {
    try {
      const data = await post('/api/scan', { dir: scanDir.trim() });
      setBatch(data.batch);
      setStatus({ type: 'success', message: `Found ${data.found} images, ${data.added} new. Ready to classify.` });
      refresh();
    } catch (e) {
      setStatus({ type: 'error', message: e instanceof Error ? e.message : String(e) });
    }
  }

  async function handleCorrect(photo: Photo, values: Record<Axis, string>) {
    try {
      await post(`/api/photos/${photo.id}/correct`, values);
      refresh();
    } catch (e) {
      setStatus({ type: 'error', message: e instanceof Error ? e.message : String(e) });
    }
  }

  const pendingCount = photos?.filter((p) => p.status === 'pending' || p.status === 'error').length ?? 0;
  const doneCount = photos?.filter((p) => p.status === 'done').length ?? 0;

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar onSettings={() => setShowSettings(true)} onHelp={() => setShowHelp(true)} />

      <main className="flex-1 p-4 max-w-[1600px] w-full mx-auto flex flex-col gap-4">
        {status && (
          <StatusBar type={status.type} message={status.message} onDismiss={() => setStatus(null)} />
        )}

        {/* Import / batch card */}
        <div className="bg-white rounded-lg p-4 flex flex-col gap-3 shadow-sm">
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="text"
              value={scanDir}
              onChange={(e) => setScanDir(e.target.value)}
              placeholder="Pick a photo folder with Browse…"
              className="flex-1 min-w-[280px] border border-gray-300 rounded px-3 py-2 text-sm font-mono"
            />
            <button
              onClick={() => setShowBrowse(true)}
              className="px-4 py-2 rounded-md text-sm font-medium border border-gray-300 hover:bg-gray-50"
            >
              Browse…
            </button>
            <ActionButton
              onClick={() => setTrainDir(scanDir.trim() || activeBatch)}
              disabled={running || (!scanDir.trim() && !activeBatch)}
              color="rgb(22, 163, 74)"
            >
              Train on this folder…
            </ActionButton>
            <ActionButton onClick={handleScan} disabled={!scanDir.trim()}>Scan Folder</ActionButton>
            {batches && batches.length > 0 && (
              <select
                value={activeBatch ?? ''}
                onChange={(e) => setBatch(e.target.value)}
                className="border border-gray-300 rounded px-2 py-2 text-sm max-w-[340px]"
              >
                {batches.map((b) => (
                  <option key={b.batch} value={b.batch}>
                    {b.batch.split('/').slice(-2).join('/')} ({b.done}/{b.total})
                  </option>
                ))}
              </select>
            )}
          </div>

          {activeBatch && (
            <div className="flex flex-wrap items-center gap-3">
              {!running ? (
                <ActionButton
                  onClick={() => post('/api/classify', { action: 'start' }).then(refresh)}
                  disabled={pendingCount === 0}
                >
                  Classify {pendingCount > 0 ? `${pendingCount} photos` : '— all done'}
                </ActionButton>
              ) : (
                <>
                  <ActionButton onClick={() => post('/api/classify', { action: 'stop' })} color="rgb(180, 60, 40)">
                    Stop
                  </ActionButton>
                  <div className="flex-1 min-w-[200px]">
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${progress ? (100 * progress.doneThisRun) / Math.max(1, progress.totalThisRun) : 0}%`,
                          backgroundColor: 'rgb(51, 102, 204)',
                        }}
                      />
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {progress?.doneThisRun}/{progress?.totalThisRun}
                      {progress?.currentName ? ` — ${progress.currentName}` : ''}
                      {progress && progress.exemplarsUsed > 0
                        ? ` · using ${progress.exemplarsUsed} correction example${progress.exemplarsUsed === 1 ? '' : 's'}`
                        : ''}
                    </div>
                  </div>
                </>
              )}
              <ActionButton onClick={() => setShowApply(true)} disabled={doneCount === 0 || running}>
                Apply Renames…
              </ActionButton>
            </div>
          )}

          {stats && stats.reviewed > 0 && (
            <div className="flex flex-wrap gap-2 text-xs text-gray-600 border-t border-gray-100 pt-3">
              <Stat label="reviewed" value={`${stats.reviewed}`} />
              {AXES.map((a) => (
                <Stat
                  key={a}
                  label={a}
                  value={`${Math.round((100 * (stats[`ok_${a}`] ?? 0)) / stats.reviewed)}%`}
                />
              ))}
              <Stat label="all four" value={`${Math.round((100 * (stats.ok_all ?? 0)) / stats.reviewed)}%`} />
              {config && (
                <Stat label="training examples" value={`${config.exemplars_available}`} />
              )}
            </div>
          )}
        </div>

        {/* Photo grid grouped by proposed name */}
        {groups.map(([name, list]) => (
          <section key={name}>
            <h2 className="font-mono font-semibold text-gray-800 mb-2">
              {name} <span className="text-gray-400 font-normal">×{list.length}</span>
            </h2>
            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))' }}>
              {list.map((p) => (
                <PhotoCard
                  key={p.id}
                  photo={p}
                  config={config!}
                  onCorrect={handleCorrect}
                  onRetry={(photo) => post('/api/classify', { action: 'start', ids: [photo.id] }).then(refresh)}
                  onZoom={setZoom}
                />
              ))}
            </div>
          </section>
        ))}

        {activeBatch === null && (
          <div className="text-center text-gray-400 py-24">
            <div className="text-lg">No photos yet</div>
            <div className="text-sm">Paste a photo folder path above and hit Scan Folder</div>
          </div>
        )}
      </main>

      {zoom && (
        <div
          className="fixed inset-0 bg-black/85 flex items-center justify-center z-50 cursor-zoom-out"
          onClick={() => setZoom(null)}
        >
          {zoom.preview && (
            <img src={`/api/preview/${zoom.preview}`} alt="" className="max-w-[92vw] max-h-[88vh] object-contain" />
          )}
        </div>
      )}

      {trainDir && config && (
        <TrainDialog
          dir={trainDir}
          sampleSize={25}
          config={config}
          onClose={() => {
            setTrainDir(null);
            refresh();
          }}
          onFinish={(message) => {
            setTrainDir(null);
            setStatus({ type: 'success', message });
            refresh();
          }}
        />
      )}
      {showBrowse && (
        <BrowseDialog
          initialPath={scanDir.trim() || '/Volumes/Synology/Shared Media'}
          onSelect={(p) => {
            setScanDir(p);
            setShowBrowse(false);
          }}
          onClose={() => setShowBrowse(false)}
        />
      )}
      {showApply && activeBatch && (
        <ApplyDialog
          batch={activeBatch}
          onClose={() => setShowApply(false)}
          onDone={(n) => {
            setShowApply(false);
            setStatus({ type: 'success', message: `Renamed ${n} files.` });
            refresh();
          }}
          onError={(m) => setStatus({ type: 'error', message: m })}
        />
      )}
      {showSettings && config && (
        <SettingsDialog config={config} onClose={() => setShowSettings(false)} />
      )}
      {showHelp && <HelpDialog onClose={() => setShowHelp(false)} />}
    </div>
  );
}

function ActionButton({
  onClick, disabled, children, color = 'rgb(51, 102, 204)',
}: {
  onClick: () => void; disabled?: boolean; children: React.ReactNode; color?: string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="px-4 py-2 rounded-md text-white text-sm font-medium disabled:opacity-40 transition-opacity"
      style={{ backgroundColor: color }}
    >
      {children}
    </button>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <span className="bg-gray-100 rounded px-2 py-1">
      <span className="text-gray-500">{label}</span>{' '}
      <span className="font-semibold text-gray-800">{value}</span>
    </span>
  );
}

function Dialog({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl w-[600px] max-w-[92vw] max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <span className="text-lg font-semibold">{title}</span>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">✕</button>
        </div>
        <div className="p-4 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function ApplyDialog({
  batch, onClose, onDone, onError,
}: {
  batch: string; onClose: () => void; onDone: (n: number) => void; onError: (m: string) => void;
}) {
  const { data } = useSWR<{ plan: { from: string; to: string }[]; count: number }>(
    ['apply-plan', batch],
    () =>
      fetch('/api/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ batch, execute: false }),
      }).then((r) => r.json())
  );

  async function execute() {
    const res = await fetch('/api/apply', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ batch, execute: true }),
    });
    const out = await res.json();
    if (!res.ok) onError(out.error ?? 'rename failed');
    else onDone(out.renamed);
  }

  return (
    <Dialog title="Apply renames" onClose={onClose}>
      {!data ? (
        <div className="text-sm text-gray-500">Building plan…</div>
      ) : data.count === 0 ? (
        <div className="text-sm text-gray-500">Nothing to rename — files already match their proposed names.</div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="text-sm text-gray-600">
            {data.count} files in <span className="font-mono text-xs">{batch}</span> will be renamed on disk:
          </div>
          <div className="border border-gray-200 rounded max-h-[45vh] overflow-y-auto text-xs font-mono">
            {data.plan.map((p, i) => (
              <div key={i} className="px-2 py-1 border-b border-gray-100 last:border-0 flex gap-2">
                <span className="text-gray-400 truncate max-w-[40%]">{p.from}</span>
                <span className="text-gray-400">→</span>
                <span className="truncate">{p.to}</span>
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm rounded border border-gray-300">Cancel</button>
            <ActionButton onClick={execute}>Rename {data.count} files</ActionButton>
          </div>
        </div>
      )}
    </Dialog>
  );
}

function SettingsDialog({ config, onClose }: { config: AppConfig; onClose: () => void }) {
  const [model, setModel] = useState(config.model);
  const [fewshot, setFewshot] = useState(config.fewshot_n);

  async function save() {
    await fetch('/api/config', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ model, fewshot_n: fewshot }),
    });
    onClose();
  }

  return (
    <Dialog title="Settings" onClose={onClose}>
      <div className="flex flex-col gap-4 text-sm">
        <label className="flex flex-col gap-1">
          <span className="font-semibold">Ollama model</span>
          <input
            value={model}
            onChange={(e) => setModel(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 font-mono text-sm"
          />
          <span className="text-xs text-gray-500">
            Must be pulled already (e.g. qwen3-vl:8b, qwen3-vl:4b). Applies to the next photo classified.
          </span>
        </label>
        <label className="flex flex-col gap-1">
          <span className="font-semibold">Correction examples per request</span>
          <input
            type="number" min={0} max={8} value={fewshot}
            onChange={(e) => setFewshot(parseInt(e.target.value || '0', 10))}
            className="border border-gray-300 rounded px-3 py-2 w-24"
          />
          <span className="text-xs text-gray-500">
            How many of your past corrections are shown to the model as worked examples on every
            request ({config.exemplars_available} available). More examples = better accuracy but slower.
            0 disables.
          </span>
        </label>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded border border-gray-300">Cancel</button>
          <ActionButton onClick={save}>Save</ActionButton>
        </div>
      </div>
    </Dialog>
  );
}

function HelpDialog({ onClose }: { onClose: () => void }) {
  const steps: [string, string][] = [
    ['1. Scan', 'Paste an absolute folder path (Synology paths work) and hit Scan Folder. Every image found — including Sony RAW — becomes a photo in that batch.'],
    ['2. Classify', 'Start classification. The local vision model tags each photo with product, accessory, setting, and camera angle, and proposes a filename. Nothing is renamed yet.'],
    ['3. Review', 'Photos are grouped by proposed name, so a wrong one stands out. If a photo is right, hit “Looks right”. If not, fix the wrong dropdown — the correction is saved instantly.'],
    ['4. Corrections train the model', 'Every correction becomes a worked example shown to the model on future requests (e.g. a DJ-10 it mistook for a Maggy). Accuracy stats at the top track how it’s doing.'],
    ['5. Apply', 'When a batch looks right, Apply Renames shows exactly what will change on disk and only proceeds when you confirm.'],
  ];
  return (
    <Dialog title="Visy Help" onClose={onClose}>
      <div className="flex flex-col gap-3 text-sm">
        {steps.map(([t, d]) => (
          <div key={t}>
            <div className="font-semibold">{t}</div>
            <div className="text-gray-600">{d}</div>
          </div>
        ))}
      </div>
    </Dialog>
  );
}
